// Includes interactions with the API
// Responsible for logic such as:
//  - Making requests to API
//  - Parsing raw API responses into something more consumable by the app
//    (e.g. massaging results, parsing yaml)
//  - Translating app state into API query strings

import fetch from "cross-fetch";
import queryString from "query-string";
import { Query } from "../../interfaces";
import { apiFieldMappings } from "../../refdata/FilterOptions";
import { ApiBaseUrl, apiResultLimit } from "./../../config";
import { filterChoices } from "./Filters";

const baseUrl = (apiPath: string) => ApiBaseUrl() + apiPath;

export const apiPost = (postBody: any, additionalHeaders?: any) => {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-rate-limit": "off",
      ...additionalHeaders,
    },
    body: JSON.stringify(postBody),
  };
};
// TODO: Fix type of 'url' to be string (not 'any')
export function fetchData(url: any, post?: any, headers?: any): Promise<any> {
  const additionalHeaders = headers || {};
  const rateLimit = { "x-rate-limit": "off", ...additionalHeaders };
  const options = post ? { ...post, headers: { ...post.headers, ...rateLimit } } : { headers: rateLimit };
  return (
    fetch(url, options)
      .then((response: any) => {
        // either throw an error if the response is bad (non-200)...
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        // ...or otherwise return the json response payload as json
        return response.json();
      })
      // massage the response as necessary
      .then((responseObject: any) => {
        return {
          body: responseObject?.body?._source,
          createTimestamp: responseObject.meta?.index?.createTimestamp,
          data: Array.isArray(responseObject.data) && responseObject.data[0]?.data
            ? responseObject.data.map((record: any) => record.data)
            : responseObject.data,
          highlight: Array.isArray(responseObject.data)
            ? responseObject.data.map((record: any) => record.highlight)
            : responseObject.data,
          totals: responseObject.totals,
        };
      })
  );
}

export const buildFilterString = (filters: Query["filters"]) => {
  const filterList: any = [];
  apiFieldMappings.arrayValues.forEach(field => {
    const filterValue = filters?.[field.filterName];
    if (filterValue) {
      if (filterValue.length) {
        const joinedStrings = filterValue
          .map((i: string) => `${i}`).join(",");
        filterList.push(`${field.apiField}:${joinedStrings}`);
      }
    }
  });
  let orReplacements = "";
  // process single-value fields
  apiFieldMappings.singleValues.forEach(field => {
    const filterValue = filters?.[field.filterName];
    if (filterValue) {
      if (!field.apiField && filterValue.includes(":")) {
        const splitFilter = filterValue.split(":");
        // Temporary Fix, adding quotes around filter value to avoid conflicts with separator
        filterList.push(`${splitFilter[0]}:"${encodeURIComponent(splitFilter[1])}"`);
      } else {
        if (filterValue[0] === "(" && filterValue[filterValue.length - 1] === ")") {
          filterList.push(`${field.apiField}:${encodeURIComponent(filterValue)}`);
        } else {
          // Temporary Fix, adding quotes around filter value to avoid conflicts with separator
          filterList.push(`${field.apiField}:"${encodeURIComponent(filterValue)}"`);
        }

      }
    }
  });
  // multi-select fields
  apiFieldMappings.multiSelect.forEach(field => {
    const filterValue = filters?.[field.filterName];

    if (filterValue && filterChoices[field.filterName]) {
      // don't specify if it is equivalent to select ALL
      if (filterValue.length !== filterChoices[field.filterName].length) {
        if (filterValue.length) {
          const orStrings = filterValue
            .map((i: string) => `${field.apiField}:"${encodeURIComponent(i)}"`)
            .join(" OR ");
          filterList.push(`(${orStrings})`);
        }
      }
    }
  });

  // multi-value fields
  apiFieldMappings.multiValues.forEach(field => {
    const filterValue = filters?.[field.filterName];

    if (filterValue) {
      if (filterValue.length !== filterChoices[field.filterName].length) {
        if (filterValue.length) {

          const joinedStrings = filterValue
            .map((i: string) => `${i}`).join(" OR ");

          orReplacements = `${field.apiField}:(`;
          filterList.push(`${field.apiField}:(${joinedStrings})`);
        }
      }
    }
  });

  apiFieldMappings.rangeSelect.forEach(field => {
    if (filters?.[field.apiField]) {
      const dateRangeFilter = `${filters[field.apiField]}:[${encodeURIComponent(
        filters[field.filterNameStart],
      )} TO ${encodeURIComponent(filters[field.filterNameEnd])}]`;
      filterList.push(dateRangeFilter);
    }
  });

  // Join filters for query string
  if (orReplacements) {
    const modifiedList = filterList
      .join(" AND ")
      .replace(
        ` AND ${orReplacements}`,
        `) OR (${filters.activeStatus ? `ACTIVE:"${filters.activeStatus}" AND ` : ""
        }${orReplacements}`,
      );
    return `${filterList.length > 1 ? "(" : ""}${modifiedList}${filterList.length > 1 ? ")" : ""
      }`;
  } else {
    return filterList.join(" AND ");
  }
};

export const buildSearchString = (search: Query["search"]) => {
  const searchList: any = [];
  // process single-value fields
  apiFieldMappings.searchValues.forEach(field => {
    const searchValue = search?.[field.searchName];
    if (searchValue) {
      searchList.push(`${field.apiField}:${searchValue}`);
    }
  });

  // Join filters for query string
  return searchList.join(" AND ");
};

export const buildQueryUrl = (apiPath: string, query: Query, noSort?: boolean) => {
  // translates query into URL string to feed to API
  let params = {};
  const filters = buildFilterString(query.filters);
  const search = buildSearchString(query.search);
  const fields = query.fixedDisplayFields
    ? query.fixedDisplayFields
      .concat(query.displayFields ? query.displayFields : [])
      .join(",")
    : undefined;
  const sort_by = !noSort && query.sort ? query.sort.sortField?.replace("sum_", "")
    .replace("count_", "") : undefined;
  const sort_order = !noSort && query.sort ? query.sort.sortOrder : undefined;
  const agg_by = query.agg ? query.agg.agg_by : undefined;
  const agg_term_fields =
    query.agg && query.agg.agg_term_fields
      ? query.agg.agg_term_fields.join(",")
      : undefined;
  const agg_sum_fields =
    query.agg && query.agg.agg_sum_fields
      ? query.agg.agg_sum_fields.join(",")
      : undefined;
  const subtotal_by =
    query.agg && query.agg.subtotal_by ? query.agg.subtotal_by : undefined;
  const total_fields =
    query.agg && query.agg.total_fields
      ? query.agg.total_fields.join(",")
      : undefined;
  // TODO: get fields from perhaps config or query to avoid returning entire set
  // const fields = query.fixedDisplayFields.concat(query.displayFields).join(",");
  const limit = query.pagination
    ? query.pagination.resultLimit
      ? query.pagination.resultLimit
      : apiResultLimit
    : apiResultLimit;
  const pageNumber = query.pagination ? query.pagination.pageNumber - 1 : 0;
  const offset = query.pagination
    ? query.pagination.pageNumber
      ? pageNumber * limit
      : 0
    : 0;
  const react = true; // Used to distinguish React API calls
  params = {
    filters,
    search,
    fields,
    agg_by,
    agg_term_fields,
    agg_sum_fields,
    subtotal_by,
    total_fields,
    sort_by,
    sort_order,
    limit,
    offset,
    react,
  };


  const paramsString = queryString.stringify(params);

  return `${baseUrl(apiPath)}?${paramsString}`;
};

export const buildCsvDowloadUrl = (apiPath: string, query: Query) => {
  // TODO: set filename based on current app or query
  return `${buildQueryUrl(
    apiPath,
    query,
  )}&format=csv&download=true&filename=bank-data`;
};
